// Styles entry: statically import main.scss so CSS variables and per-host rules are present.
// Theme switching will be done by toggling body/#app classes; no runtime CSS injection is required.
import './main.scss'

// Icons
import 'virtual:svg-icons-register'

import { createApp } from 'vue'
import { createPinia } from 'pinia'
import ErrorMessage from './components/organisms/ErrorMessage/ErrorMessage.vue'
import App from './App.vue'
import router, { dynamicRoutes } from './router'
import i18n from '@/locales/dictionary'
import mitt from 'mitt';
import { useAppConfigurationStore } from '@/stores/useAppConfigurationStore'
import { useHostNameStore } from './stores/useHostNameStore'
import { createGtm } from '@gtm-support/vue-gtm';
import * as Sentry from "@sentry/vue";
import Popper from "vue3-popper";
import "./popper-theme.css"; 
import { useAuthenticationStore } from '@/stores/useAuthenticationStore'
import { useUserStore } from '@/stores/userStore'
import { saveCriticalState, restoreCriticalState, clearRestorationFlag } from './utils/stateRescue'
import { useVersionPolling } from '@/composables/useVersionPolling'
import { isNavigationFailure } from 'vue-router'
import { isAxiosNetworkError } from '@/utils/axiosErrors'
import { isRouteModuleLoadError } from '@/utils/routeModuleErrors'
import {
  isBrowserExtensionRejection,
  isLegacyGoogleMapsLoadError,
  isMsalTransientAuthError,
  isNonDanishClient,
  SENTRY_IGNORE_ERROR_PATTERNS,
} from '~shared/errors/sentryNoise'

function applyDocumentLanguage(locale: string | undefined) {
  document.documentElement.lang = 'da'
}

// Expose app version to window for tooltip and debugging
// @ts-ignore - __APP_VERSION__ is injected by Vite define
if (typeof __APP_VERSION__ !== 'undefined' && typeof window !== 'undefined') {
    window.__APP_VERSION__ = __APP_VERSION__
}

/**
 * CRITICAL: Break MSAL redirect loops IMMEDIATELY before any other code runs
 * This must run BEFORE Vue app initialization to prevent infinite redirect loops
 */
function breakMSALRedirectLoop() {
  const urlParams = new URLSearchParams(window.location.hash.substring(1))
  const hasAuthParams = urlParams.has('state') && urlParams.has('code')
  
  if (hasAuthParams) {
    // Check if we've been in a redirect loop
    const loopKey = 'msal_redirect_loop_detected'
    const loopCount = parseInt(sessionStorage.getItem(loopKey) || '0')
    
    if (loopCount > 2) {
      console.error('🛑 MSAL REDIRECT LOOP DETECTED - Breaking loop by clearing all auth state')
      
      // Nuclear option: clear ALL MSAL data
      Object.keys(localStorage).forEach(key => {
        if (key.includes('msal') || key.includes('login.windows')) {
          localStorage.removeItem(key)
        }
      })
      
      Object.keys(sessionStorage).forEach(key => {
        if (key.includes('msal') || key.includes('login.windows')) {
          sessionStorage.removeItem(key)
        }
      })
      
      // Clear the URL and redirect to home
      sessionStorage.removeItem(loopKey)
      window.location.href = window.location.origin
      return
    }
    
    // Increment loop counter
    sessionStorage.setItem(loopKey, (loopCount + 1).toString())
    
    // Reset counter after successful handling (will be cleared by MSAL if it works)
    setTimeout(() => {
      const currentCount = parseInt(sessionStorage.getItem(loopKey) || '0')
      if (currentCount > 0) {
        sessionStorage.setItem(loopKey, '0')
      }
    }, 5000)
  } else {
    // No auth params, clear counter
    sessionStorage.removeItem('msal_redirect_loop_detected')
  }
}

// Run MSAL loop breaker FIRST if this is the primary instance
const hasExistingInstance = !!(window as any).__APP_INITIALIZED__;

if (!hasExistingInstance) {
  (window as any).__APP_INITIALIZED__ = true;
  
  /**
   * DOCUMENTED VITE CHUNK LOAD ERROR HANDLING
   * This is the official way to handle failed dynamic imports in Vite 4+.
   * It catches errors when a new version is deployed and old chunks are missing.
   * See: https://vite.dev/guide/build.html#load-error-handling
   */
  window.addEventListener('vite:preloadError', (event) => {
    console.error('🔴 Vite preload error detected (Chunk Load Error). Reloading...', event);
    
    // Prevent the error from bubbling up to the console as an unhandled exception
    event.preventDefault();
    
    // Use session storage to prevent infinite reload loops (e.g. if the user is offline)
    const RELOAD_KEY = 'app_reload_attempted';
    const lastReload = sessionStorage.getItem(RELOAD_KEY);
    const now = Date.now();
    
    if (lastReload && (now - parseInt(lastReload)) < 10000) {
      console.error('🛑 Multiple preload errors in 10s. Stopping automatic reload.');
      return;
    }
    
    // Set a global flag to indicate reload is in progress
    // This prevents Vue Router from trying to resolve components while reloading
    (window as any).__VITE_RELOAD_IN_PROGRESS__ = true;
    
    // Try to rescue state before reloading
    saveCriticalState();

    sessionStorage.setItem(RELOAD_KEY, now.toString());
    window.location.reload();
  });

  breakMSALRedirectLoop()

  // Catch unhandled promise rejections to debug any silent errors
  window.addEventListener('unhandledrejection', (event) => {
    // Expected when a guard aborts SPA navigation (e.g. handoff to Nuxt SSR for event pages)
    if (isNavigationFailure(event.reason)) {
      event.preventDefault()
      return
    }

    // Vue Router often surfaces chunk/version skew as uncaught promise rejections (Sentry: handled=no)
    if (isAxiosNetworkError(event.reason)) {
      event.preventDefault()
      console.error('🔴 Axios network error (unhandledrejection):', event.reason)
      return
    }

    if (
      isMsalTransientAuthError(event.reason) ||
      isBrowserExtensionRejection(event.reason) ||
      isLegacyGoogleMapsLoadError(event.reason)
    ) {
      event.preventDefault()
      return
    }

    if (isRouteModuleLoadError(event.reason)) {
      event.preventDefault()
      console.error('🔴 Route module load failed (unhandledrejection). Reloading...', event.reason)
      const RELOAD_KEY = 'app_reload_attempted'
      const lastReload = sessionStorage.getItem(RELOAD_KEY)
      const now = Date.now()
      if (!lastReload || now - parseInt(lastReload, 10) >= 10000) {
        ;(window as any).__VITE_RELOAD_IN_PROGRESS__ = true
        sessionStorage.setItem(RELOAD_KEY, now.toString())
        window.location.reload()
      }
      return
    }

    console.error('🚨🚨🚨 [main.ts] UNHANDLED PROMISE REJECTION:')
    console.error('🚨 reason:', event.reason)
    console.error('🚨 promise:', event.promise)
    console.trace('🚨 Stack trace:')
  })

  /**
   * Early Storage Migration (V1 → V2 → V3)
 * Runs BEFORE app initialization to ensure existing users get migrated even with cached code
 * This is critical for deployments where users won't do hard refresh
 * 
 * Version History:
 * - V1: Basic tab tracking with timestamp only
 * - V2: Added isAwake field for sleep detection
 * - V3: Added routeName field for route-specific tab tracking
 */
function migrateStorageToV3IfNeeded() {
  const STORAGE_VERSION_KEY = 'dn_tab_sync_version'
  const ACTIVE_TABS_KEY = 'dn_active_tabs'
  const CURRENT_STORAGE_VERSION = 3
  
  try {
    const storedVersion = localStorage.getItem(STORAGE_VERSION_KEY)
    const currentVersion = storedVersion ? parseInt(storedVersion) : 1
    
    // If already on latest version, nothing to do
    if (currentVersion >= CURRENT_STORAGE_VERSION) {
      return
    }
    
    // Clear all old storage keys completely to avoid conflicts
    // Better to lose old tab tracking than have incompatible data structures
    localStorage.removeItem(STORAGE_VERSION_KEY)
    localStorage.removeItem(ACTIVE_TABS_KEY)
    localStorage.removeItem('dn_tab_sync') // Old sync key from v1
    
    // Initialize fresh v3 storage (empty, will be populated when composable loads)
    localStorage.setItem(STORAGE_VERSION_KEY, CURRENT_STORAGE_VERSION.toString())
    localStorage.setItem(ACTIVE_TABS_KEY, JSON.stringify([]))
  } catch (error) {
    console.error('Failed to initialize storage in main.ts:', error)
    // Even on error, try to ensure version is set so migration won't run again
    try {
      localStorage.setItem(STORAGE_VERSION_KEY, CURRENT_STORAGE_VERSION.toString())
      console.warn('⚠️ Stored version marker despite error, will use empty storage')
    } catch (finalError) {
      console.error('Failed to set storage version marker:', finalError)
    }
  }
}

// Run migration BEFORE app creation - this is critical!
migrateStorageToV3IfNeeded()

// Global host redirects (permanent): happens before page renders
// Preserve protocol, path, query string and hash.
// Mapping: events.dn.dk → arrangementer.dn.dk
//          events.affaldsindsamlingen.dk → mineindsamlinger.affaldsindsamlingen.dk
try {
  const currentHost = window.location.hostname
  const redirectMap: Record<string, string> = {
    'events.dn.dk': 'arrangementer.dn.dk',
    'events.affaldsindsamlingen.dk': 'mineindsamlinger.affaldsindsamlingen.dk'
  }
  
  if (redirectMap[currentHost]) {
    const destHost = redirectMap[currentHost]
    const protocol = window.location.protocol || 'https:'
    const path = window.location.pathname || '/'
    const search = window.location.search || ''
    const hash = window.location.hash || ''
    const dest = `${protocol}//${destHost}${path}${search}${hash}`
    // Use replace so history isn't polluted with the redirect source
    window.location.replace(dest)
  }
} catch (e) {
  // If anything goes wrong (e.g. running in SSR environment), do nothing
}

function getEffectiveHostClass(): string {
  try {
    const emulated = localStorage.getItem('theme-switcher-emulated-host')
    if (emulated && emulated.length > 0) {
      const hostRaw = emulated.replace(/^v2\./, '').replace(/^staging\./, '')
      return hostRaw === 'localhost' ? 'localhost' : hostRaw.replace(/\./g, '-')
    }
  } catch (e) {
    // ignore localStorage errors
  }
  const hostRaw = window.location.hostname.replace(/^v2\./, '').replace(/^staging\./, '')
  return hostRaw === 'localhost' ? 'localhost' : hostRaw.replace(/\./g, '-')
}

// main.scss is imported above so CSS vars and host-specific rules are present before app mounts

window.onerror = function(message, source, lineno, colno, error) {
  if (typeof clarity === "function") {
      clarity("set", "error", {
          message: message,
          source: source,
          lineno: lineno,
          colno: colno,
          stack: error?.stack || "No stack trace",
      });
  } else {
      console.warn("Clarity is not installed. Error details:", {
          message: message,
          source: source,
          lineno: lineno,
          colno: colno,
          stack: error?.stack || "No stack trace",
      });
  }
};


const isProduction = import.meta.env.VITE_NODE_ENV === 'production';

const eventBus = mitt();
const app = createApp(App)

app.config.errorHandler = (err, instance, info) => {
  const error = err as Error;
  if (isRouteModuleLoadError(error)) {
    console.error('🔴 Route module load failed (Vue errorHandler):', error.message, info)
    return
  }
  if (isAxiosNetworkError(error)) {
    console.error('🔴 Axios network error (Vue errorHandler):', error.message, info)
    return
  }
  if (isMsalTransientAuthError(error)) {
    console.warn('[msal] Auth error (transient):', error.message)
    return
  }
  if (typeof clarity === "function") {
      clarity("set", "error", {
          message: error.message,
          stack: error.stack,
          info,
      });
  } else {
      console.error("Vue Error:", {
          message: error.message,
          stack: error.stack,
          info,
      });
  }
};

app.component("Popper", Popper)

app.provide('eventBus', eventBus);

if (isProduction) {
  Sentry.init({
    app,
    dsn: "https://271dc905ddf1ca59ea730c05a6452a27@o4508691902169088.ingest.de.sentry.io/4508691956039760",
    // @ts-ignore - __APP_VERSION__ is defined by Vite define
    release: typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : 'unknown',
    initialScope: {
      tags: {
        // Harvesting the version as a tag makes it easier to filter/search in Sentry
        // @ts-ignore - __APP_VERSION__ is defined by Vite define
        app_version: typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : 'unknown',
        environment: 'production'
      }
    },
    integrations: [
      Sentry.browserTracingIntegration({ router }),
      Sentry.replayIntegration(),
    ],
    // Tracing
    tracesSampleRate: 1.0, //  Capture 100% of the transactions
    // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
    // We only enable this for localhost to avoid CORS issues with custom Sentry headers (sentry-trace, baggage)
    // on the production API until the backend CORS policy is updated to allow these headers.
    tracePropagationTargets: ["localhost"],
    // Session Replay
    replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
    replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
    ignoreErrors: [...SENTRY_IGNORE_ERROR_PATTERNS],
    // We only serve Danish users — drop bot/crawler traffic from other regions.
    beforeSend: (event) => (isNonDanishClient() ? null : event),
    beforeSendTransaction: (event) => (isNonDanishClient() ? null : event),
  });
}

const pinia = createPinia()
app.use(pinia)

// Try to restore any state rescued from a previous reload (e.g. chunk load error)
restoreCriticalState();

app.use(i18n)

const hostNameStore = useHostNameStore()
const appConfigurationStore = useAppConfigurationStore()

applyDocumentLanguage(String(i18n.global.locale))

dynamicRoutes(router)

// CRITICAL: Add restoration guard BEFORE app.use(router)
// This ensures it runs before any component-level guards (beforeRouteEnter)
let restorationNavigationCompleted = false;

router.beforeEach((to, from, next) => {
  const restoringFlag = sessionStorage.getItem('__state_restoration_in_progress__');
  
  if (restoringFlag === 'true') {
    // Don't delay - just allow navigation but flag stays set for component guards to check
    next();
  } else {
    next();
  }
});

router.afterEach((to, from) => {
  // After first navigation completes when restoration flag is set, clear it
  const restoringFlag = sessionStorage.getItem('__state_restoration_in_progress__');
  if (restoringFlag === 'true' && !restorationNavigationCompleted) {
    restorationNavigationCompleted = true;
    // Small delay to ensure component guards have finished
    setTimeout(() => {
      clearRestorationFlag();
    }, 100);
  }
});

app.use(router)

//replaceRoute(router)
//console.log('routes' + JSON.stringify(router.getRoutes()))

app.use(
  createGtm({
    id: "GTM-5B2CH2JR",
    vueRouter: router
  })
)

// Dev-only theme switcher: mount a tiny UI for quick theme testing.
// We mount it only when not in production to avoid shipping it to users.
if (!isProduction) {
  // Dynamic import ensures this file isn't bundled into the production main bundle by some bundlers.
  import('./components/dev/ThemeSwitcher.vue').then(({ default: ThemeSwitcher }) => {
    try {
      const container = document.createElement('div')
      container.id = 'theme-switcher'
      document.body.appendChild(container)
      const switcherApp = createApp(ThemeSwitcher)
      switcherApp.mount('#theme-switcher')
    } catch (err) {
      // Fail silently in dev if mounting the switcher fails
      // eslint-disable-next-line no-console
      console.warn('Failed to mount ThemeSwitcher', err)
    }
  })
}

app.component('ErrorMessage', ErrorMessage)

// Initialize authentication BEFORE mounting the app
// This ensures logged-in state is detected immediately, even when opening app in new tab
const authStore = useAuthenticationStore()
authStore.initialize().then(() => {
  if (authStore.isLoggedIn) {
    void useUserStore().loadUser()
  }

  // Set Sentry user context after authentication is initialized
  const userId = authStore.userId
  const userEmail = authStore.userEmail
  const userFirstName = authStore.userFirstName
  const userLastname = authStore.userLastname
  
  if (userId) {
    Sentry.setUser({
      id: userId,
      email: userEmail ?? undefined,
      username: `${userFirstName} ${userLastname}`.trim() || undefined
    })
  } else {
    // Anonymous user
    Sentry.setUser(null)
  }
  
  // Mount app only after authentication state is initialized
  app.mount('#app')
  
  // Start version polling after app is mounted
  // This checks for new versions every 5 minutes and shows a non-blocking banner
  const { startPolling } = useVersionPolling()
  startPolling()
}).catch((error) => {
  // Even if initialization fails, still mount the app
  console.error('Failed to initialize authentication:', error)
  app.mount('#app')
  
  // Still start version polling even if auth fails
  const { startPolling } = useVersionPolling()
  startPolling()
})

router.isReady().then(() => {
  //  app.mount('#app')
});
}
